class RootsAnnouncementBar extends HTMLElement {
  #current = 0;
  #interval;
  #countdownInterval;

  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('focusin', this.#suspendForInteraction);
    this.addEventListener('focusout', this.#resumeAfterInteraction);

    if (this.pauseOnHover) {
      this.addEventListener('mouseenter', this.#suspendForInteraction);
      this.addEventListener('mouseleave', this.#resumeAfterInteraction);
    }

    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#syncSlides();
    this.#updateCountdowns();
    this.#startCountdowns();
    this.play();
  }

  disconnectedCallback() {
    this.pause();
    clearInterval(this.#countdownInterval);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  get slides() {
    return Array.from(this.querySelectorAll('[data-roots-announcement-slide]'));
  }

  get autoRotate() {
    return this.dataset.autoRotate === 'true';
  }

  get interval() {
    const interval = Number.parseInt(this.dataset.interval || '', 10);
    return Number.isNaN(interval) ? 5000 : interval;
  }

  get pauseOnHover() {
    return this.dataset.pauseOnHover === 'true';
  }

  get reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  play() {
    if (!this.autoRotate || this.slides.length < 2 || this.reducedMotion) return;

    this.pause();
    this.#interval = window.setInterval(() => {
      if (document.hidden) return;

      this.next();
    }, this.interval);
  }

  pause() {
    window.clearInterval(this.#interval);
    this.#interval = undefined;
  }

  next() {
    this.#show(this.#current + 1);
  }

  previous() {
    this.#show(this.#current - 1);
  }

  #show(index) {
    const slides = this.slides;
    if (!slides.length) return;

    this.#current = ((index % slides.length) + slides.length) % slides.length;
    this.#syncSlides();
  }

  #syncSlides() {
    const slides = this.slides;
    const onlyOneSlide = slides.length <= 1;

    slides.forEach((slide, index) => {
      const isHidden = index !== this.#current;
      slide.setAttribute('aria-hidden', `${isHidden}`);
      slide.toggleAttribute('inert', isHidden);
    });

    this.querySelectorAll('[data-roots-announcement-action]').forEach((button) => {
      button.toggleAttribute('disabled', onlyOneSlide);
    });
  }

  #startCountdowns() {
    if (!this.querySelector('[data-roots-announcement-countdown]')) return;

    this.#countdownInterval = window.setInterval(() => this.#updateCountdowns(), 1000);
  }

  #updateCountdowns() {
    this.querySelectorAll('[data-roots-announcement-countdown]').forEach((countdown) => {
      const targetValue = countdown.getAttribute('data-countdown-target');
      const target = targetValue ? new Date(targetValue).getTime() : Number.NaN;

      if (Number.isNaN(target)) {
        countdown.hidden = true;
        return;
      }

      const remaining = target - Date.now();
      if (remaining <= 0) {
        this.#setExpired(countdown);
        return;
      }

      countdown.hidden = false;
      const totalSeconds = Math.floor(remaining / 1000);
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      this.#setPart(countdown, 'days', days);
      this.#setPart(countdown, 'hours', hours);
      this.#setPart(countdown, 'minutes', minutes);
      this.#setPart(countdown, 'seconds', seconds);
    });
  }

  #setPart(countdown, part, value) {
    const element = countdown.querySelector(`[data-countdown-part="${part}"]`);
    if (!element) return;

    element.textContent = `${value}`.padStart(2, '0');
  }

  #setExpired(countdown) {
    const expiredText = countdown.getAttribute('data-expired-text') || 'Ended';
    countdown.textContent = expiredText;
    countdown.hidden = false;
  }

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest('[data-roots-announcement-action]');
    if (!button || !this.contains(button)) return;

    const action = button.getAttribute('data-roots-announcement-action');
    if (action === 'previous') this.previous();
    if (action === 'next') this.next();
    this.play();
  };

  #suspendForInteraction = () => this.pause();

  #resumeAfterInteraction = (event) => {
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;

    this.play();
  };

  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.pause();
    } else {
      this.play();
    }
  };
}

if (!customElements.get('roots-announcement-bar')) {
  customElements.define('roots-announcement-bar', RootsAnnouncementBar);
}
